import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/hooks/use-toast';

interface FileValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  suggestions: string[];
}

interface FileMetadata {
  name: string;
  size: number;
  type: string;
  lastModified: number;
  estimatedProcessingTime: number;
  confidenceScore: number;
  detectedSupplier?: string;
  detectedServiceType?: string;
  detectedAmount?: number;
  extractedData?: any;
}

interface AdvancedUploadProgress {
  id: string;
  file: File;
  metadata: FileMetadata;
  validation: FileValidationResult;
  status: 'validating' | 'uploading' | 'processing' | 'preview' | 'completed' | 'error';
  progress: number;
  error?: string;
  filePath?: string;
  previewData?: any;
}

export const useAdvancedFileUpload = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [uploads, setUploads] = useState<Map<string, AdvancedUploadProgress>>(new Map());
  const [batchMode, setBatchMode] = useState(false);
  const [autoProcess, setAutoProcess] = useState(true);

  // Advanced file validation
  const validateFile = useCallback(async (file: File): Promise<FileValidationResult> => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const suggestions: string[] = [];

    // Size validation
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      errors.push(`File size ${(file.size / 1024 / 1024).toFixed(1)}MB exceeds maximum of 50MB`);
    }

    // Type validation
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      errors.push(`File type ${file.type} is not supported. Use PDF, JPEG, PNG, or WebP`);
    }

    // File structure validation for PDFs
    if (file.type === 'application/pdf') {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const header = String.fromCharCode(...uint8Array.slice(0, 4));
        
        if (header !== '%PDF') {
          errors.push('Invalid PDF file structure');
        }
      } catch (error) {
        warnings.push('Could not verify PDF structure');
      }
    }

    // Image validation
    if (file.type.startsWith('image/')) {
      if (file.size < 10 * 1024) { // Less than 10KB
        warnings.push('Image file seems very small - ensure text is readable');
      }
      
      // Check if image might be a screenshot vs scan
      if (file.name.toLowerCase().includes('screenshot')) {
        suggestions.push('Consider scanning the original document for better OCR accuracy');
      }
    }

    // Filename analysis
    if (file.name.includes('copy') || file.name.includes('duplicate')) {
      warnings.push('Filename suggests this might be a duplicate');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      suggestions
    };
  }, []);

  // Extract metadata from file
  const extractMetadata = useCallback(async (file: File): Promise<FileMetadata> => {
    const baseMetadata = {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      estimatedProcessingTime: 0,
      confidenceScore: 0
    };

    // Estimate processing time based on file size and type
    let processingTime = 2000; // Base 2 seconds
    if (file.type === 'application/pdf') {
      processingTime += file.size / (1024 * 1024) * 1500; // +1.5s per MB for PDFs
    } else {
      processingTime += file.size / (1024 * 1024) * 1000; // +1s per MB for images
    }

    // Try to detect supplier/service type from filename
    const filename = file.name.toLowerCase();
    let detectedSupplier: string | undefined;
    let detectedServiceType: string | undefined;

    // Common supplier patterns
    const supplierPatterns = [
      { pattern: /pg&e|pacific.?gas/i, name: 'Pacific Gas & Electric', service: 'gas' },
      { pattern: /edison|sce/i, name: 'Southern California Edison', service: 'electricity' },
      { pattern: /ladwp/i, name: 'Los Angeles DWP', service: 'water' },
      { pattern: /att|at&t/i, name: 'AT&T', service: 'internet' },
      { pattern: /verizon/i, name: 'Verizon', service: 'internet' },
      { pattern: /comcast|xfinity/i, name: 'Comcast Xfinity', service: 'internet' }
    ];

    for (const { pattern, name, service } of supplierPatterns) {
      if (pattern.test(filename)) {
        detectedSupplier = name;
        detectedServiceType = service;
        break;
      }
    }

    return {
      ...baseMetadata,
      estimatedProcessingTime: Math.round(processingTime),
      confidenceScore: detectedSupplier ? 0.7 : 0.3,
      detectedSupplier,
      detectedServiceType
    };
  }, []);

  // Quick preview extraction (simplified)
  const extractPreviewData = useCallback(async (file: File) => {
    // This would typically involve calling an OCR service
    // For now, return mock data based on detected patterns
    const metadata = await extractMetadata(file);
    
    if (metadata.detectedSupplier) {
      return {
        supplierName: metadata.detectedSupplier,
        serviceType: metadata.detectedServiceType,
        estimatedAmount: Math.random() * 500 + 50,
        billDate: new Date().toISOString().split('T')[0],
        confidence: metadata.confidenceScore
      };
    }

    return null;
  }, [extractMetadata]);

  // Process file through validation pipeline
  const processFile = useCallback(async (file: File): Promise<string | null> => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to upload files.",
        variant: "destructive"
      });
      return null;
    }

    const fileId = crypto.randomUUID();
    
    // Initialize upload state
    const metadata = await extractMetadata(file);
    setUploads(prev => new Map(prev.set(fileId, {
      id: fileId,
      file,
      metadata,
      validation: { isValid: false, errors: [], warnings: [], suggestions: [] },
      status: 'validating',
      progress: 0
    })));

    try {
      // Step 1: Validation
      const validation = await validateFile(file);
      setUploads(prev => {
        const upload = prev.get(fileId);
        if (upload) {
          prev.set(fileId, { ...upload, validation, progress: 20 });
        }
        return new Map(prev);
      });

      if (!validation.isValid) {
        setUploads(prev => {
          const upload = prev.get(fileId);
          if (upload) {
            prev.set(fileId, { 
              ...upload, 
              status: 'error', 
              error: validation.errors.join(', ') 
            });
          }
          return new Map(prev);
        });
        return null;
      }

      // Step 2: Upload to storage
      setUploads(prev => {
        const upload = prev.get(fileId);
        if (upload) {
          prev.set(fileId, { ...upload, status: 'uploading', progress: 40 });
        }
        return new Map(prev);
      });

      const fileName = `${user.id}/${fileId}-${file.name}`;
      const { data, error } = await supabase.storage
        .from('bills')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get the full public URL instead of returning relative path
      const { data: publicUrlData } = supabase.storage
        .from('bills')
        .getPublicUrl(data.path);

      // Step 3: Extract preview data
      setUploads(prev => {
        const upload = prev.get(fileId);
        if (upload) {
          prev.set(fileId, { ...upload, status: 'processing', progress: 70 });
        }
        return new Map(prev);
      });

      const previewData = await extractPreviewData(file);

      // Step 4: Complete or show preview
      setUploads(prev => {
        const upload = prev.get(fileId);
        if (upload) {
          prev.set(fileId, { 
            ...upload, 
            status: autoProcess ? 'completed' : 'preview',
            progress: 100,
            filePath: publicUrlData.publicUrl,
            previewData
          });
        }
        return new Map(prev);
      });

      if (validation.warnings.length > 0 || validation.suggestions.length > 0) {
        toast({
          title: "Upload completed with notes",
          description: `${validation.warnings.length} warnings, ${validation.suggestions.length} suggestions`,
        });
      } else {
        toast({
          title: "Upload successful",
          description: `${file.name} processed successfully`
        });
      }

      return publicUrlData.publicUrl;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      setUploads(prev => {
        const upload = prev.get(fileId);
        if (upload) {
          prev.set(fileId, { ...upload, status: 'error', error: errorMessage });
        }
        return new Map(prev);
      });

      toast({
        title: "Upload failed",
        description: errorMessage,
        variant: "destructive"
      });
      return null;
    }
  }, [user, validateFile, extractMetadata, extractPreviewData, autoProcess, toast]);

  // Batch processing
  const processBatch = useCallback(async (files: File[]) => {
    setBatchMode(true);
    
    toast({
      title: "Batch processing started",
      description: `Processing ${files.length} files...`
    });

    const results = await Promise.allSettled(
      files.map(file => processFile(file))
    );

    const successful = results.filter(r => r.status === 'fulfilled' && r.value).length;
    const failed = results.length - successful;

    toast({
      title: "Batch processing completed",
      description: `${successful} successful, ${failed} failed`
    });

    setBatchMode(false);
  }, [processFile, toast]);

  // Utility functions
  const removeUpload = useCallback((fileId: string) => {
    setUploads(prev => {
      const newMap = new Map(prev);
      newMap.delete(fileId);
      return newMap;
    });
  }, []);

  const clearCompleted = useCallback(() => {
    setUploads(prev => {
      const newMap = new Map();
      prev.forEach((upload, id) => {
        if (upload.status !== 'completed') {
          newMap.set(id, upload);
        }
      });
      return newMap;
    });
  }, []);

  const retryUpload = useCallback(async (fileId: string) => {
    const upload = uploads.get(fileId);
    if (upload) {
      await processFile(upload.file);
    }
  }, [uploads, processFile]);

  const approvePreview = useCallback(async (fileId: string) => {
    setUploads(prev => {
      const upload = prev.get(fileId);
      if (upload && upload.status === 'preview') {
        prev.set(fileId, { ...upload, status: 'completed' });
      }
      return new Map(prev);
    });
  }, []);

  return {
    uploads: Array.from(uploads.entries()).map(([id, upload]) => upload),
    processFile,
    processBatch,
    removeUpload,
    clearCompleted,
    retryUpload,
    approvePreview,
    batchMode,
    setBatchMode,
    autoProcess,
    setAutoProcess
  };
};